import {
  AlertColor,
  Breakpoint,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ModalProps,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { PropsWithChildren, ReactElement } from "react";
import CloseIcon from '@mui/icons-material/Close';
import AlertNotification from "../AlertNotification";

export type BaseDialogButtonColor =
  | "inherit"
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "info"
  | "warning";

export interface BaseDialogProps extends PropsWithChildren {
  alertColor?: AlertColor;
  alertText?: string | ReactElement;
  actionButtons?: ReactElement;
  contentText?: string | ReactElement;
  open: boolean;
  onCloseDialogClicked: () => void;
  sx?: SxProps<Theme>;
  title: string;
  /**
   * If `true`, the dialog stretches to `maxWidth`.
   *
   * Notice that the dialog width grow is limited by the default margin.
   * @default false
   */
  fullWidth?: boolean;

  /**
   * If `true`, the dialog will be full-screen.
   */
  fullScreen?: boolean;

  /**
   * Determine the max-width of the dialog.
   * The dialog width grows with the size of the screen.
   * Set to `false` to disable `maxWidth`.
   * @default 'sm'
   */
  maxWidth?: Breakpoint | false;
  paperProps?: {
    style: {
      height: number;
      maxHeight: number;
    }
  }
}

export default function BaseDialog(props: BaseDialogProps) {
  const {
    alertColor = "success",
    alertText,
    actionButtons,
    contentText,
    open,
    onCloseDialogClicked,
    sx,
    title,
    children,
    fullWidth,
    fullScreen,
    maxWidth,
    paperProps
  } = props;

  const theme = useTheme();

  const onCloseDialog: ModalProps["onClose"] = (event, reason) => {
    if (reason && reason === "backdropClick") {
      return;
    }

    onCloseDialogClicked();
  };

  return (
    <Dialog
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      sx={sx}
      open={open}
      onClose={onCloseDialog}
      PaperProps={paperProps}
      fullScreen={fullScreen}
    >
      <DialogTitle align="left">
        <Stack direction="row" justifyContent="left" alignItems="left">
          <Typography variant="h4" >{title}</Typography>
          <IconButton sx={{ ml: 'auto' }} size="small" onClick={() => onCloseDialogClicked()}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {contentText && <DialogContentText color={theme.palette.text.primary}>{contentText}</DialogContentText>}
        {alertText && (
          <AlertNotification severity={alertColor} sx={{ mt: theme.spacing(2) }}>
            {alertText}
          </AlertNotification>
        )}
        {children}
      </DialogContent>
      {actionButtons && <DialogActions>
        {actionButtons}
      </DialogActions>}
    </Dialog>

  );
}
